import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search, Filter, Download, Eye, Edit2, Trash2, Plus, Loader2,
  Building2, Phone, Mail, MapPin, CheckCircle2, AlertTriangle, FileText,
  ShieldCheck, ShieldAlert, Share2, Copy, MessageCircle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import Pagination from '../components/ui/pagination';

const CustomerList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [kycFilter, setKycFilter] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('is_active', statusFilter === 'active' ? 'true' : 'false');
      if (searchQuery) params.append('search', searchQuery);
      if (kycFilter !== 'all') params.append('kyc_status', kycFilter);
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));
      const response = await fetch(`${backendUrl}/api/customers?${params}`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch customers');
      const data = await response.json();
      setCustomers(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch customers", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [backendUrl, getAuthHeaders, statusFilter, searchQuery, kycFilter, page, pageSize, toast]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);
  useEffect(() => { setPage(1); }, [statusFilter, kycFilter]);
  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); fetchCustomers(); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line

  const displayedCustomers = useMemo(() => {
    if (roleFilter === 'all') return customers;
    return customers.filter(c => {
      const roles = (c.business_roles || ['SHIPPER']).map(r => String(r).toUpperCase());
      return roles.includes(roleFilter);
    });
  }, [customers, roleFilter]);

  const handleDelete = async () => {
    if (!customerToDelete) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/customers/${customerToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete customer');
      }
      
      toast({
        title: "Success",
        description: "Customer deleted successfully"
      });
      
      fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast({
        title: "Error",
        description: "Failed to delete customer",
        variant: "destructive"
      });
    } finally {
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  const openDeleteDialog = (customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };

  // Helper for role styling
  const getRoleBadgeStyle = (rKey) => {
    switch (rKey) {
      case 'AGENT':
        return 'bg-amber-50 text-amber-800 border-amber-200';
      case 'FORWARDER':
        return 'bg-purple-50 text-purple-800 border-purple-200';
      case 'CONSIGNEE':
        return 'bg-sky-50 text-sky-800 border-sky-200';
      default:
        return 'bg-emerald-50 text-emerald-800 border-emerald-200';
    }
  };

  const getRoleLabel = (rKey) => {
    switch (rKey) {
      case 'SHIPPER': return 'Shipper';
      case 'CONSIGNEE': return 'Consignee';
      case 'FORWARDER': return 'Forwarder';
      case 'AGENT': return 'Agent';
      default: return rKey;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-dark font-manrope">Customer Management</h1>
          <p className="text-text-secondary font-ibm mt-1">
            Enterprise customer registry, branches & KYC compliance documents
          </p>
        </div>
        <Button
          data-testid="add-customer-button"
          onClick={() => navigate('/customers/add')}
          className="bg-accent-green hover:bg-accent-green/90 text-white font-ibm font-semibold h-11 px-6 rounded-lg shadow-sm shrink-0"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Filter and Control Bar */}
      <Card className="bg-primary-blue text-white rounded-xl shadow-lg border-0">
        <div className="p-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/10 rounded-lg">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold font-manrope">
                  Customer Directory
                </h3>
                <p className="text-xs text-white/70 font-ibm">
                  Total {total} registered customers
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative min-w-[220px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/60" />
                <Input
                  data-testid="customer-search-input"
                  placeholder="Search by name, PAN, CIN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-full bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/40 font-ibm h-10 text-sm"
                />
              </div>

              {/* KYC Status Filter */}
              <Select value={kycFilter} onValueChange={setKycFilter}>
                <SelectTrigger 
                  data-testid="kyc-filter-select"
                  className="w-36 bg-white/10 border-white/20 text-white focus:bg-white/20 focus:border-white/40 font-ibm h-10 text-sm"
                >
                  <SelectValue placeholder="KYC Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All KYC</SelectItem>
                  <SelectItem value="pending">KYC Pending</SelectItem>
                  <SelectItem value="verified">KYC Verified</SelectItem>
                </SelectContent>
              </Select>

              {/* Role Filter */}
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger 
                  data-testid="role-filter-select"
                  className="w-36 bg-white/10 border-white/20 text-white focus:bg-white/20 focus:border-white/40 font-ibm h-10 text-sm"
                >
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="SHIPPER">Shipper</SelectItem>
                  <SelectItem value="CONSIGNEE">Consignee</SelectItem>
                  <SelectItem value="FORWARDER">Forwarder</SelectItem>
                  <SelectItem value="AGENT">Agent</SelectItem>
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger 
                  data-testid="status-filter-select"
                  className="w-32 bg-white/10 border-white/20 text-white focus:bg-white/20 focus:border-white/40 font-ibm h-10 text-sm"
                >
                  <Filter className="w-3.5 h-3.5 mr-1.5 opacity-70" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              {/* Export Button */}
              <Button
                data-testid="export-customers-button"
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white font-ibm font-medium h-10 text-sm"
              >
                <Download className="w-3.5 h-3.5 mr-1.5" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Modern, Streamlined Customer Table */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
            <span className="ml-3 text-text-secondary font-ibm text-sm">Loading customer directory...</span>
          </div>
        ) : displayedCustomers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <div className="w-14 h-14 bg-gray-50 rounded-full flex items-center justify-center mb-3">
              <Building2 className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-base font-semibold text-text-dark font-manrope">No customers match your criteria</p>
            <p className="text-xs text-text-secondary font-ibm mt-1 max-w-sm mb-4">
              Try adjusting your search keywords, role selection, or KYC filter.
            </p>
            <Button
              onClick={() => navigate('/customers/add')}
              className="bg-accent-green hover:bg-accent-green/90 text-white font-ibm text-sm"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add New Customer
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-200/80 text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">
                  <th className="py-3.5 px-6 w-[30%]">Customer / Entity</th>
                  <th className="py-3.5 px-6 w-[23%]">Primary Contact</th>
                  <th className="py-3.5 px-6 w-[18%]">Location & Branches</th>
                  <th className="py-3.5 px-6 w-[17%]">KYC Status</th>
                  <th className="py-3.5 px-4 text-center w-[6%]">Status</th>
                  <th className="py-3.5 px-6 text-right w-[6%]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-ibm">
                {displayedCustomers.map((customer) => {
                  const roles = (customer.business_roles || ['SHIPPER']).map(r => String(r).toUpperCase());
                  const isKycVerified = customer.kyc_status === 'VERIFIED';
                  const completedKyc = customer.kyc_completed_count || 0;
                  const requiredKyc = customer.kyc_required_count || 6;
                  const missingDocs = customer.missing_kyc_docs || [];

                  return (
                    <tr
                      key={customer.id}
                      className="hover:bg-blue-50/30 transition-colors group"
                    >
                      {/* Customer / Entity */}
                      <td className="py-4 px-6 align-top">
                        <div className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => navigate(`/customers/view/${customer.id}`)}
                            className="text-left font-semibold text-text-dark group-hover:text-accent-blue text-sm transition-colors cursor-pointer"
                          >
                            {customer.business_name}
                          </button>
                          
                          {/* Role Badges */}
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {roles.map((rKey, i) => (
                              <span
                                key={i}
                                className={`text-[10px] font-semibold px-2 py-0.5 rounded border uppercase tracking-wider ${getRoleBadgeStyle(rKey)}`}
                              >
                                {getRoleLabel(rKey)}
                              </span>
                            ))}
                          </div>

                          {/* Tax Identifiers */}
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-text-secondary font-mono">
                            {customer.pan_number && (
                              <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded border border-gray-200">
                                PAN: {customer.pan_number}
                              </span>
                            )}
                            {customer.head_office_gst && (
                              <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded border border-gray-200">
                                GST: {customer.head_office_gst}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Primary Contact */}
                      <td className="py-4 px-6 align-top">
                        <div className="space-y-1 text-sm">
                          {customer.contact_person ? (
                            <p className="font-medium text-text-dark">{customer.contact_person}</p>
                          ) : (
                            <p className="text-gray-400 italic text-xs">No contact person</p>
                          )}

                          {customer.contact_phone && (
                            <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                              <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <a href={`tel:${customer.contact_phone}`} className="hover:text-accent-blue hover:underline">
                                {customer.contact_phone}
                              </a>
                            </div>
                          )}

                          {customer.contact_email && (
                            <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                              <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              <a href={`mailto:${customer.contact_email}`} className="hover:text-accent-blue hover:underline truncate max-w-[200px]">
                                {customer.contact_email}
                              </a>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Location & Branches */}
                      <td className="py-4 px-6 align-top">
                        <div className="space-y-1.5">
                          {customer.head_office_location ? (
                            <div className="flex items-start gap-1.5 text-xs text-text-dark font-medium">
                              <MapPin className="w-3.5 h-3.5 text-accent-blue shrink-0 mt-0.5" />
                              <span>{customer.head_office_location}</span>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 italic">No head office set</p>
                          )}

                          <div>
                            <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                              {customer.branch_count || 1} {customer.branch_count === 1 ? 'Branch' : 'Branches'}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* KYC Status */}
                      <td className="py-4 px-6 align-top">
                        <div className="space-y-1.5">
                          {isKycVerified ? (
                            <span
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-300"
                              title="All required KYC documents have been verified"
                            >
                              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                              KYC Verified
                            </span>
                          ) : (
                            <div>
                              <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-300"
                                title={`Pending required KYC documents (${completedKyc}/${requiredKyc})`}
                              >
                                <ShieldAlert className="w-3.5 h-3.5 text-amber-600" />
                                KYC Pending ({completedKyc}/{requiredKyc})
                              </span>

                              {/* Missing Documents Preview */}
                              {missingDocs.length > 0 && (
                                <div className="mt-1.5 text-[11px] text-amber-800 bg-amber-50/60 p-1.5 rounded border border-amber-200/60 leading-tight">
                                  <span className="font-semibold text-amber-900">Missing:</span>{' '}
                                  {missingDocs.slice(0, 2).join(', ')}
                                  {missingDocs.length > 2 && (
                                    <span className="text-amber-700 font-medium"> +{missingDocs.length - 2} more</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100 mt-1.5">
                            <button
                              type="button"
                              onClick={() => navigate(`/customers/edit/${customer.id}#kyc`)}
                              className="text-[11px] text-accent-blue hover:underline font-medium"
                            >
                              {isKycVerified ? 'Manage' : 'Upload →'}
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              type="button"
                              onClick={() => {
                                const url = `${window.location.origin}/kyc-onboarding/${customer.kyc_token}`;
                                const text = `Dear *${customer.business_name}*,\n\nPlease complete your company KYC onboarding and upload required documents (IEC, PAN, GST, etc.) using this secure link:\n${url}\n\n- Stavan Logistics`;
                                const phone = customer.contact_phone ? customer.contact_phone.replace(/[^0-9]/g, '') : '';
                                const waUrl = phone ? `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(text)}` : `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
                                window.open(waUrl, '_blank');
                              }}
                              className="text-[11px] text-emerald-700 hover:text-emerald-800 hover:underline font-medium flex items-center gap-0.5"
                              title="Share KYC link via WhatsApp"
                            >
                              <MessageCircle className="w-3 h-3 text-emerald-600" /> WhatsApp
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              type="button"
                              onClick={() => {
                                const url = `${window.location.origin}/kyc-onboarding/${customer.kyc_token}`;
                                navigator.clipboard.writeText(url);
                                toast({ title: 'Copied', description: 'Public KYC link copied to clipboard.' });
                              }}
                              className="text-[11px] text-slate-500 hover:text-slate-800 hover:underline font-medium flex items-center gap-0.5"
                              title="Copy public KYC link"
                            >
                              <Copy className="w-3 h-3" /> Copy
                            </button>
                          </div>
                        </div>
                      </td>

                      {/* Account Status */}
                      <td className="py-4 px-4 text-center align-top">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            customer.is_active
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-gray-100 text-gray-600 border border-gray-200'
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${customer.is_active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                          {customer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-4 px-6 text-right align-top">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            data-testid={`view-customer-${customer.id}`}
                            onClick={() => navigate(`/customers/view/${customer.id}`)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 text-gray-500 hover:text-accent-blue rounded-lg transition-colors"
                            title="View Profile & Dockets"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`edit-customer-${customer.id}`}
                            onClick={() => navigate(`/customers/edit/${customer.id}`)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-amber-50 text-gray-500 hover:text-amber-600 rounded-lg transition-colors"
                            title="Edit Customer & KYC"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`delete-customer-${customer.id}`}
                            onClick={() => openDeleteDialog(customer)}
                            className="w-8 h-8 flex items-center justify-center hover:bg-red-50 text-gray-500 hover:text-accent-red rounded-lg transition-colors"
                            title="Delete Customer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && customers.length > 0 && (
          <div className="border-t border-gray-100 px-6 py-3">
            <Pagination
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
            />
          </div>
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{customerToDelete?.business_name}"? 
              This will also delete all associated branches and KYC documents. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-accent-red hover:bg-accent-red/90 text-white font-ibm"
            >
              Delete Customer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomerList;
